// ============================================================================
// FSA NOTE MODAL — LEFT SIDEBAR
// ============================================================================
// Left-menu navigation for the Note Modal. Drives pane switching and shows
// the sticky summary card at the bottom (totals + reconciliation badge).

import React from 'react';
import IconSystem from 'icon-collection/icon-systems';

export type NotePane =
  | 'overview'
  | 'ledgers'
  | 'main-note'
  | 'disclosures'
  | 'auditor-notes';

interface SidebarItem {
  id: NotePane;
  label: string;
  icon: React.ReactNode;
  badge?: string | number;
  /**
   * Secondary amber pip rendered next to the primary badge — used by
   * the Ledgers row to surface pending (unclassified) ledger count so
   * the auditor sees outstanding work without opening the pane.
   * Tooltip on hover explains the number.
   */
  pendingBadge?: number;
  pendingBadgeTooltip?: string;
}

interface Props {
  activePane: NotePane;
  onSelect: (pane: NotePane) => void;
  /** Total ledgers mapped to this Note (classified + unclassified). */
  ledgersCount: number;
  /** Unclassified ledgers still requiring action. */
  ledgersPending: number;
  disclosuresCount: number;
  summary: {
    /** Note Total CY (actual, full precision). */
    cActual: number;
    /** Note Total PY (actual). */
    oActual: number;
    /** Note Total CY (auditor-saved rounded). */
    cRounded: number;
    /** Note Total PY (auditor-saved rounded). */
    oRounded: number;
    /**
     * FSA-2.4 N3 — Effective ledger amount (after cross-note adjustments).
     * Both actual + rounded surfaced so the sidebar can mirror the
     * header Actual/Rounded toggle. Rounded is auto-rounded from
     * the actual (Effective Ledger isn't auditor-entered, so no
     * separate stored rounded value exists).
     */
    effectiveLedgerCyActual: number;
    effectiveLedgerPyActual: number;
    effectiveLedgerCyRounded: number;
    effectiveLedgerPyRounded: number;
    /** Authoritative actual drift (|noteTotal − effectiveLedger|). */
    driftCyActual: number;
    driftPyActual: number;
    /** Rounded drift (|noteTotalRounded − effectiveLedgerRounded|). */
    driftCyRounded: number;
    driftPyRounded: number;
  };
  /**
   * FSA-2.4 — current display mode from the header toggle. Sidebar
   * mirrors so it always reads consistently with the table cells.
   */
  displayMode: 'actual' | 'rounded';
  /**
   * Engagement-level rounding tolerance. Used to colour the rounded
   * drift row ✓ when within tolerance, ⚠ otherwise. Actual drift uses
   * the hard 0.005 threshold (half-paisa).
   */
  roundingTolerance: number;
}

const NoteSidebar: React.FC<Props> = ({
  activePane,
  onSelect,
  ledgersCount,
  ledgersPending,
  disclosuresCount,
  summary,
  displayMode,
  roundingTolerance,
}) => {
  // FSA-2.4 — pick the values that match the header toggle. Actual
  // uses the strict 0.005 threshold (half-paisa floating-point safety,
  // must tally exactly per locked spec). Rounded uses the engagement
  // tolerance — auditor's rounded edits are allowed to drift within
  // tolerance and still read as ✓.
  const isRounded = displayMode === 'rounded';
  const effCy = isRounded ? summary.effectiveLedgerCyRounded : summary.effectiveLedgerCyActual;
  const effPy = isRounded ? summary.effectiveLedgerPyRounded : summary.effectiveLedgerPyActual;
  const noteCy = isRounded ? summary.cRounded : summary.cActual;
  const notePy = isRounded ? summary.oRounded : summary.oActual;
  const driftCy = isRounded ? summary.driftCyRounded : summary.driftCyActual;
  const driftPy = isRounded ? summary.driftPyRounded : summary.driftPyActual;
  const driftThreshold = isRounded ? roundingTolerance : 0.005;
  const items: SidebarItem[] = [
    { id: 'overview',      label: 'Overview',              icon: <IconSystem.home size={14} /> },
    {
      id: 'ledgers',
      label: 'Ledgers',
      icon: <IconSystem.list size={14} />,
      badge: ledgersCount,
      pendingBadge: ledgersPending,
      pendingBadgeTooltip: ledgersPending > 0
        ? `${ledgersPending} ledger${ledgersPending === 1 ? '' : 's'} mapped to this Note still need a class.`
        : undefined,
    },
    { id: 'main-note',     label: 'Main Note',             icon: <IconSystem.edit size={14} /> },
    { id: 'disclosures',   label: 'Additional Disclosures',icon: <IconSystem.folder size={14} />,  badge: disclosuresCount },
    { id: 'auditor-notes', label: 'Reviewer Notes',        icon: <IconSystem.lock size={14} /> },
  ];

  return (
    <aside style={sidebarStyle}>
      <nav style={navStyle}>
        {items.map(item => {
          const active = activePane === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => onSelect(item.id)}
              style={navItemStyle(active)}
            >
              <span style={{ display: 'inline-flex', color: active ? '#2563eb' : 'var(--text-secondary)' }}>
                {item.icon}
              </span>
              <span style={{ flex: 1, textAlign: 'left' }}>{item.label}</span>
              {item.badge !== undefined && item.badge > 0 && (
                <span style={badgeStyle(active)}>{item.badge}</span>
              )}
              {item.pendingBadge !== undefined && item.pendingBadge > 0 && (
                <span
                  style={pendingBadgeStyle}
                  title={item.pendingBadgeTooltip}
                >
                  {item.pendingBadge}
                </span>
              )}
            </button>
          );
        })}

        <div style={dividerStyle} />

        <div style={futureGroupTitleStyle}>Audit (coming soon)</div>
        <div style={{ ...navItemStyle(false), opacity: 0.5, cursor: 'not-allowed' }}>
          <IconSystem.trendingUp size={14} />
          <span style={{ flex: 1, textAlign: 'left' }}>Comparison (PY vs CY)</span>
        </div>
        <div style={{ ...navItemStyle(false), opacity: 0.5, cursor: 'not-allowed' }}>
          <IconSystem.message size={14} />
          <span style={{ flex: 1, textAlign: 'left' }}>Review Queries</span>
        </div>
        <div style={{ ...navItemStyle(false), opacity: 0.5, cursor: 'not-allowed' }}>
          <IconSystem.tag size={14} />
          <span style={{ flex: 1, textAlign: 'left' }}>Working Paper Ref</span>
        </div>
        <div style={{ ...navItemStyle(false), opacity: 0.5, cursor: 'not-allowed' }}>
          <IconSystem.check size={14} />
          <span style={{ flex: 1, textAlign: 'left' }}>Sign-off</span>
        </div>
      </nav>

      {/* FSA-2.4 N1 — sidebar summary rebuilt.
          Old layout showed `Total CY / Total PY / Recon ✓` where "Total"
          was actually the raw signed note total — credit-balance notes
          rendered negative which confused auditors ("what is this
          number?"). New layout makes the three quantities explicit:

            LEDGER SUM   raw TB sum (live), absolute display
            NOTE TOTAL   note's saved total, absolute display
            DRIFT        |LedgerSum − NoteTotal|, ✓ when within tolerance

          Absolute display (Math.abs) keeps the figures positive
          regardless of debit/credit-balance convention so the
          auditor reads "is the ledger 4 lakhs and the note 4 lakhs"
          without sign juggling.

          PY ledger sum is now surfaced (was hidden); PY drift is
          shown but not yet authoritative — drift evaluator v2 (N3)
          will rework the reconciliation logic to cover cross-note
          flows, sign-sweeps, and PL→Reserves. */}
      <div style={summaryFooterStyle}>
        {/* FSA-2.4 — mode badge so the auditor sees at a glance which
            set of figures they're reading (matches the header toggle). */}
        <div style={modeBadgeStyle(isRounded)}>
          {isRounded ? 'Rounded' : 'Actual'}
        </div>
        {/* Effective Ledger: TB + JE + cross-note in/out + inbound PL.
            Reflects what the auditor expects the ledger to contribute
            to this Note after all legitimate transfers. */}
        <SummaryGroup
          label="Effective Ledger"
          cy={Math.abs(effCy)}
          py={Math.abs(effPy)}
        />
        {/* Note Amount: what's saved on the Note — includes manual
            adjustments the auditor entered (computed cells, manual
            disclosures, user-added classes). */}
        <SummaryGroup
          label="Note Amount"
          cy={Math.abs(noteCy)}
          py={Math.abs(notePy)}
          bold
        />
        <SummaryDriftGroup
          driftCy={driftCy}
          driftPy={driftPy}
          threshold={driftThreshold}
        />
      </div>
    </aside>
  );
};

// FSA-2.4 N1 — sidebar summary sub-components.
// Pulled out so the JSX above reads as a flat list of three groups
// (Ledger Sum / Note Total / Drift) instead of nine inline divs.
const SummaryGroup: React.FC<{
  label: string;
  cy: number;
  py: number;
  bold?: boolean;
}> = ({ label, cy, py, bold }) => (
  <div style={summaryGroupStyle}>
    <div style={summaryGroupLabelStyle}>{label}</div>
    <div style={summaryLineStyle}>
      <span style={summaryAxisLabelStyle}>CY</span>
      <span style={{ ...summaryAmountStyle, fontWeight: bold ? 700 : 500 }}>
        ₹ {fmtAmt(cy)}
      </span>
    </div>
    <div style={summaryLineStyle}>
      <span style={summaryAxisLabelStyle}>PY</span>
      <span style={{ ...summaryAmountStyle, fontWeight: bold ? 700 : 500 }}>
        ₹ {fmtAmt(py)}
      </span>
    </div>
  </div>
);

const SummaryDriftGroup: React.FC<{
  driftCy: number;
  driftPy: number;
  /**
   * FSA-2.4 — drift threshold for ✓/⚠ classification. Actual mode uses
   * 0.005 (half-paisa, must tally exactly). Rounded mode uses the
   * engagement-level rounding tolerance — rounded edits are allowed
   * to drift slightly without flagging as an exception.
   */
  threshold: number;
}> = ({ driftCy, driftPy, threshold }) => {
  const cyOk = driftCy <= threshold;
  const pyOk = driftPy <= threshold;
  const allOk = cyOk && pyOk;
  return (
    <div style={summaryGroupStyle}>
      <div style={summaryGroupLabelStyle}>
        Drift{' '}
        <span
          style={{
            color: allOk ? '#16a34a' : '#d97706',
            marginLeft: 4,
          }}
          title={allOk
            ? 'Note amount matches the effective ledger (cross-note adjusted)'
            : 'Note amount differs from the effective ledger — see Deviations panel for breakdown'}
        >
          {allOk ? '✓' : '⚠'}
        </span>
      </div>
      <div style={summaryLineStyle}>
        <span style={summaryAxisLabelStyle}>CY</span>
        <span style={{
          ...summaryAmountStyle,
          color: cyOk ? '#16a34a' : '#d97706',
          fontWeight: 600,
        }}>
          ₹ {fmtAmt(driftCy)}
        </span>
      </div>
      <div style={summaryLineStyle}>
        <span style={summaryAxisLabelStyle}>PY</span>
        <span style={{
          ...summaryAmountStyle,
          color: pyOk ? '#16a34a' : '#d97706',
          fontWeight: 600,
        }}>
          ₹ {fmtAmt(driftPy)}
        </span>
      </div>
    </div>
  );
};

const fmtAmt = (v: number) =>
  v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ───── styles ────────────────────────────────────────────────────────────────

const sidebarStyle: React.CSSProperties = {
  width: 220,
  minWidth: 220,
  borderRight: '1px solid var(--border-color)',
  display: 'flex',
  flexDirection: 'column',
  background: 'var(--bg-secondary)',
};
const navStyle: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: '8px 6px',
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};
const navItemStyle = (active: boolean): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '7px 10px',
  background: active ? 'var(--bg-primary)' : 'transparent',
  border: 'none',
  borderLeft: active ? '3px solid #2563eb' : '3px solid transparent',
  color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
  cursor: 'pointer',
  fontSize: '0.82rem',
  fontWeight: active ? 600 : 500,
  textAlign: 'left',
  borderRadius: '0 4px 4px 0',
});
const badgeStyle = (active: boolean): React.CSSProperties => ({
  fontSize: '0.68rem',
  padding: '1px 6px',
  borderRadius: 999,
  background: active ? '#2563eb' : 'var(--border-color)',
  color: active ? 'white' : 'var(--text-secondary)',
  fontWeight: 700,
  minWidth: 20,
  textAlign: 'center',
});
// Secondary "pending" pip — amber so the auditor reads it as
// outstanding-work even at a glance. Sits next to the primary badge
// without replacing it, so the auditor sees `[12] [3]` = 12 total, 3 pending.
const pendingBadgeStyle: React.CSSProperties = {
  fontSize: '0.68rem',
  padding: '1px 6px',
  borderRadius: 999,
  background: '#fef3c7',
  color: '#92400e',
  fontWeight: 700,
  minWidth: 20,
  textAlign: 'center',
  marginLeft: 4,
  border: '1px solid #fbbf24',
  cursor: 'help',
};
const dividerStyle: React.CSSProperties = {
  height: 1,
  background: 'var(--border-color)',
  margin: '10px 0',
};
const futureGroupTitleStyle: React.CSSProperties = {
  fontSize: '0.65rem',
  fontWeight: 700,
  letterSpacing: 0.5,
  textTransform: 'uppercase',
  color: 'var(--text-secondary)',
  padding: '4px 10px',
  marginBottom: 4,
};
const summaryFooterStyle: React.CSSProperties = {
  borderTop: '1px solid var(--border-color)',
  padding: '10px 12px',
  background: 'var(--bg-primary)',
  fontSize: '0.76rem',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};
// FSA-2.4 N1 — three-group summary (Ledger Sum / Note Total / Drift).
// Each group has a small uppercase label + two CY/PY value rows.
const summaryGroupStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
};
const summaryGroupLabelStyle: React.CSSProperties = {
  fontSize: '0.66rem',
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  color: 'var(--text-secondary)',
  marginBottom: 2,
};
const summaryLineStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};
const summaryAxisLabelStyle: React.CSSProperties = {
  color: 'var(--text-secondary)',
  fontSize: '0.7rem',
  fontWeight: 500,
};
const summaryAmountStyle: React.CSSProperties = {
  fontVariantNumeric: 'tabular-nums',
};
// FSA-2.4 — mode badge above the sidebar summary. Subtle pill that
// confirms which figures the auditor is reading (mirrors the header
// Actual/Rounded toggle). Colour differs slightly so the eye picks
// up the mode at a glance without re-reading the label.
const modeBadgeStyle = (rounded: boolean): React.CSSProperties => ({
  alignSelf: 'flex-start',
  fontSize: '0.62rem',
  fontWeight: 700,
  letterSpacing: 0.5,
  textTransform: 'uppercase',
  padding: '1px 8px',
  borderRadius: 999,
  background: rounded ? '#dbeafe' : '#e5e7eb',
  color: rounded ? '#1e40af' : '#374151',
  marginBottom: 2,
});

export default NoteSidebar;
